import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getConversationTrace, getScoreDetails, type ConversationTrace } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Cpu,
  Search,
  CheckCircle2,
  AlertCircle,
  Zap,
  FileSearch,
  Brain,
  Sparkles,
  Loader2,
  DollarSign,
  Hash,
  MessageSquare,
  Eye,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { SlackPreview } from '@/components/golden-answer';
import { Button } from '@/components/ui/button';

// Type for search result with content
interface SearchResultWithContent {
  id: string;
  source: string;
  title: string;
  url?: string;
  score: number;
  combinedScore?: number;
  contentPreview?: string;
  content?: string;
}

interface AgentTraceProps {
  conversationId: string;
}

interface ScoreInfo {
  score: number;
  details?: Record<string, number>;
  reasoning?: string;
  judgeType?: string;
  judgeModel?: string;
}

// Component to display expandable search results with content
function ExpandableSearchResults({ results }: { results: SearchResultWithContent[] }) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [showAllItems, setShowAllItems] = useState(false);

  const toggleItem = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const displayedResults = showAllItems ? results : results.slice(0, 5);

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground">
          Retrieved Documents ({results.length}):
        </span>
        {results.length > 5 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-5 text-[10px]"
            onClick={() => setShowAllItems(!showAllItems)}
          >
            {showAllItems ? 'Show less' : `Show all ${results.length}`}
          </Button>
        )}
      </div>
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {displayedResults.map((result) => {
          const isExpanded = expandedItems.has(result.id);
          const hasFullContent = result.content && result.content.length > 0;
          const displayContent = isExpanded ? result.content : result.contentPreview;

          return (
            <div key={result.id} className="border rounded-lg overflow-hidden bg-background">
              <div className="p-2 flex items-center gap-2 bg-muted/30">
                <Badge variant="outline" className="text-[10px]">{result.source}</Badge>
                <span className="flex-1 text-xs font-medium truncate">{result.title}</span>
                <span className="text-[10px] text-muted-foreground">
                  {(result.score * 100).toFixed(0)}%
                </span>
                {hasFullContent && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-2 text-[10px]"
                    onClick={() => toggleItem(result.id)}
                  >
                    {isExpanded ? (
                      <>
                        <ChevronDown className="h-3 w-3 mr-1" />
                        Collapse
                      </>
                    ) : (
                      <>
                        <Eye className="h-3 w-3 mr-1" />
                        Expand
                      </>
                    )}
                  </Button>
                )}
              </div>
              {displayContent && (
                <div className={cn(
                  "p-2 text-xs border-t",
                  isExpanded ? "max-h-[300px] overflow-y-auto" : "max-h-[50px] overflow-hidden"
                )}>
                  <p className="whitespace-pre-wrap text-muted-foreground">
                    {displayContent}
                    {!isExpanded && result.content && result.content.length > 200 && (
                      <span className="text-blue-500 ml-1">...</span>
                    )}
                  </p>
                </div>
              )}
              {result.url && isExpanded && (
                <div className="px-2 pb-2 text-[10px] text-muted-foreground border-t pt-1">
                  <a href={result.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    {result.url}
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AgentTrace({ conversationId }: AgentTraceProps) {
  const { data: trace, isLoading, error } = useQuery({
    queryKey: ['conversation-trace', conversationId],
    queryFn: () => getConversationTrace(conversationId),
    enabled: !!conversationId,
  });

  // Fetch scores for the execution if we have an executionId
  const { data: scoresData } = useQuery({
    queryKey: ['execution-scores', trace?.executionId],
    queryFn: () => trace?.executionId ? getScoreDetails(trace.executionId) : Promise.resolve(null),
    enabled: !!trace?.executionId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading trace data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-red-500">
        <AlertCircle className="h-5 w-5 mr-2" />
        <span className="text-sm">Failed to load trace data</span>
      </div>
    );
  }

  if (!trace?.hasTrace) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileSearch className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">{trace?.message || 'No trace data available for this conversation.'}</p>
        <p className="text-xs mt-1 opacity-75">Trace data is only available for conversations after the Agent Trace feature was enabled.</p>
      </div>
    );
  }

  // Parse scores from the score records
  const scores: Record<string, ScoreInfo> = {};
  if (scoresData?.records) {
    for (const record of scoresData.records) {
      const scorerType = record.scorerType;
      let details: Record<string, number> | undefined;
      try {
        details = typeof record.details === 'string' ? JSON.parse(record.details) : record.details;
      } catch {
        details = undefined;
      }
      scores[scorerType] = {
        score: record.score,
        details,
        reasoning: record.reasoning,
        judgeType: record.judgeType,
        judgeModel: record.judgeModel,
      };
    }
  }

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <TraceSummaryBar trace={trace} />

      {/* Visual Pipeline Overview */}
      <PipelineOverview trace={trace} scores={scores} />

      {/* Timeline */}
      <TraceTimeline trace={trace} scores={scores} />

      {/* Quality Insights */}
      <QualityInsights trace={trace} />
    </div>
  );
}

function TraceSummaryBar({ trace }: { trace: ConversationTrace }) {
  const formatDuration = (ms?: number) => {
    if (!ms) return '—';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatCost = (cost?: number) => {
    if (!cost) return '—';
    if (cost < 0.001) return '<$0.001';
    return `$${cost.toFixed(4)}`;
  };

  return (
    <div className="flex flex-wrap gap-4 p-3 bg-muted/30 rounded-lg border">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">
          {formatDuration(trace.summary?.totalDurationMs)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Hash className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm">
          {(trace.summary?.totalInputTokens || 0) + (trace.summary?.totalOutputTokens || 0)} tokens
        </span>
      </div>
      <div className="flex items-center gap-2">
        <DollarSign className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm">
          {formatCost(trace.summary?.estimatedCostUsd)}
        </span>
      </div>
      <div className="ml-auto">
        <Badge variant={trace.status === 'completed' ? 'default' : 'destructive'}>
          {trace.status === 'completed' ? (
            <>
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Completed
            </>
          ) : (
            <>
              <AlertCircle className="h-3 w-3 mr-1" />
              {trace.status}
            </>
          )}
        </Badge>
      </div>
    </div>
  );
}

// Visual pipeline overview showing all phases with scores
function PipelineOverview({ trace, scores }: { trace: ConversationTrace; scores: Record<string, ScoreInfo> }) {
  const getScoreColor = (score?: number) => {
    if (score === undefined || score === null) return 'bg-muted-foreground/20';
    if (score >= 0.8) return 'text-green-600 bg-green-100 dark:bg-green-900/30';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30';
    return 'text-red-600 bg-red-100 dark:bg-red-900/30';
  };

  const formatDuration = (ms?: number) => {
    if (ms === undefined || ms === null) return 'Skipped';
    if (ms === 0) return '<1ms';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  // Calculate duration split for reasoner/formatter (70/30 based on typical token distribution)
  const phase4Duration = trace.phase4?.durationMs;
  const reasonerDuration = phase4Duration ? Math.round(phase4Duration * 0.7) : undefined;
  const formatterDuration = phase4Duration ? Math.round(phase4Duration * 0.3) : undefined;

  const phases = [
    {
      key: 'query_expander',
      name: 'Query Expander',
      icon: Zap,
      color: 'text-blue-500',
      durationMs: trace.phase1?.durationMs,
      score: scores.query_expander?.score,
      hasData: !!trace.phase1,
    },
    {
      key: 'retrieval',
      name: 'Retrieval',
      icon: Search,
      color: 'text-green-500',
      durationMs: trace.phase2?.durationMs,
      score: scores.retrieval?.score,
      hasData: !!trace.phase2,
    },
    {
      key: 'assessment',
      name: 'Assessment',
      icon: FileSearch,
      color: 'text-yellow-500',
      durationMs: trace.phase3?.durationMs,
      score: undefined, // Assessment is deterministic
      hasData: !!trace.phase3,
    },
    {
      key: 'reasoner',
      name: 'Reasoner',
      icon: Brain,
      color: 'text-purple-500',
      durationMs: reasonerDuration,
      score: scores.reasoner?.score,
      hasData: !!(trace.phase4?.data.reasonerThinking || trace.phase4?.data.reasonerAnswer),
    },
    {
      key: 'formatter',
      name: 'Formatter',
      icon: Sparkles,
      color: 'text-pink-500',
      durationMs: formatterDuration,
      score: scores.formatter?.score,
      hasData: !!trace.phase4?.data.formattedResponse,
    },
  ];

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Cpu className="h-4 w-4" />
          Pipeline Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between overflow-x-auto pb-2">
          {phases.map((phase, i) => (
            <div key={phase.key} className="flex items-center">
              <div className="flex flex-col items-center min-w-[80px]">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center mb-1",
                  phase.hasData ? getScoreColor(phase.score) : "bg-muted-foreground/10"
                )}>
                  <phase.icon className={cn("h-5 w-5", phase.hasData ? phase.color : "text-muted-foreground/50")} />
                </div>
                <span className="text-xs font-medium text-center">{phase.name}</span>
                {phase.score !== undefined && (
                  <span className={cn("text-xs font-bold", getScoreColor(phase.score).split(' ')[0])}>
                    {Math.round(phase.score * 100)}%
                  </span>
                )}
                <span className={cn(
                  "text-[10px]",
                  phase.hasData ? "text-muted-foreground" : "text-muted-foreground/50"
                )}>
                  {phase.hasData ? formatDuration(phase.durationMs) : 'Skipped'}
                </span>
              </div>
              {i < phases.length - 1 && (
                <ChevronRight className="h-4 w-4 text-muted-foreground mx-1 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
        {/* End-to-end score if available */}
        {scores.end_to_end && (
          <div className="mt-3 pt-3 border-t flex items-center justify-center gap-2">
            <span className="text-xs text-muted-foreground">End-to-End Score:</span>
            <Badge className={cn("text-sm", getScoreColor(scores.end_to_end.score))}>
              {Math.round(scores.end_to_end.score * 100)}%
            </Badge>
            {scores.end_to_end.judgeType === 'llm' && (
              <span className="text-xs text-blue-600">(AI Judge)</span>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground text-center mt-2">
          Click phases below for detailed inputs, outputs, and AI judge reasoning
        </p>
      </CardContent>
    </Card>
  );
}

function TraceTimeline({ trace, scores }: { trace: ConversationTrace; scores: Record<string, ScoreInfo> }) {
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());

  const togglePhase = (key: string) => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Calculate duration split for reasoner/formatter
  const phase4Duration = trace.phase4?.durationMs;
  const reasonerDuration = phase4Duration ? Math.round(phase4Duration * 0.7) : undefined;
  const formatterDuration = phase4Duration ? Math.round(phase4Duration * 0.3) : undefined;

  // Build 5 phases: Query Expander, Vector Search, Assessor, Reasoner, Formatter
  const phases: Array<{
    key: string;
    scorerKey: string;
    name: string;
    data: Record<string, unknown> | null;
    icon: typeof Zap;
    color: string;
    durationMs?: number;
    model?: string;
  }> = [
    {
      key: 'phase1',
      scorerKey: 'query_expander',
      name: 'Query Expander',
      data: trace.phase1 ? { ...trace.phase1.data } : null,
      icon: Zap,
      color: 'text-blue-500',
      durationMs: trace.phase1?.durationMs,
      model: trace.phase1?.model,
    },
    {
      key: 'phase2',
      scorerKey: 'retrieval',
      name: 'Vector Search',
      data: trace.phase2 ? { ...trace.phase2.data } : null,
      icon: Search,
      color: 'text-green-500',
      durationMs: trace.phase2?.durationMs,
    },
    {
      key: 'phase3',
      scorerKey: 'assessment',
      name: 'Assessor',
      data: trace.phase3 ? { ...trace.phase3.data } : null,
      icon: FileSearch,
      color: 'text-yellow-500',
      durationMs: trace.phase3?.durationMs,
      model: trace.phase3?.model,
    },
    {
      key: 'phase4a',
      scorerKey: 'reasoner',
      name: 'Reasoner',
      data: trace.phase4?.data.reasonerThinking || trace.phase4?.data.reasonerAnswer ? {
        thinking: trace.phase4.data.reasonerThinking,
        answer: trace.phase4.data.reasonerAnswer,
      } : null,
      icon: Brain,
      color: 'text-purple-500',
      durationMs: reasonerDuration,
      model: trace.phase4?.reasonerModel,
    },
    {
      key: 'phase4b',
      scorerKey: 'formatter',
      name: 'Formatter',
      data: (trace.phase4?.data.formattedResponse || trace.phase4?.formatterModel) ? {
        formattedResponse: trace.phase4?.data.formattedResponse,
        fallbackUsed: trace.phase4?.data.fallbackUsed,
      } : null,
      icon: Sparkles,
      color: 'text-pink-500',
      durationMs: formatterDuration,
      model: trace.phase4?.formatterModel,
    },
  ];

  const formatDuration = (ms?: number) => {
    if (ms === undefined || ms === null) return '—';
    if (ms === 0) return '<1ms';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getScoreColor = (score?: number) => {
    if (score === undefined || score === null) return '';
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getModelShortName = (model?: string) => {
    if (!model) return 'Unknown';
    const sanitizedModel = model?.startsWith('openrouter/')
      ? model.slice('openrouter/'.length)
      : model;

    const nameMap: Record<string, string> = {
      'google/gemini-2.5-flash-lite': 'Gemini 2.5 Flash Lite',
      'google/gemini-2.5-flash': 'Gemini 2.5 Flash',
      'deepseek/deepseek-r1': 'DeepSeek R1',
      'deepseek/deepseek-r1-distill-qwen-32b': 'DeepSeek R1 32B',
      'openai/o4-mini': 'o4-mini',
      'openai/gpt-4.1': 'GPT-4.1',
      'openai/gpt-4.1-nano': 'GPT-4.1 Nano',
      'anthropic/claude-haiku-4.5': 'Claude Haiku 4.5',
      'anthropic/claude-sonnet-4.5': 'Claude Sonnet 4.5',
      'amazon/nova-2-lite-v1:free': 'Nova 2 Lite',
      'x-ai/grok-4.1-fast': 'Grok 4.1 Fast',
      'text-embedding-3-small': 'Ada 3 Small',
    };
    return nameMap[sanitizedModel || ''] || sanitizedModel?.split('/').pop() || 'Unknown';
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Cpu className="h-4 w-4" />
          Workflow Timeline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {phases.map(({ key, scorerKey, name, data, icon: Icon, color, durationMs, model }) => {
          if (!data) return null;
          const isExpanded = expandedPhases.has(key);
          const scoreInfo = scores[scorerKey];

          return (
            <div key={key} className="border rounded-lg overflow-hidden">
              <button
                onClick={() => togglePhase(key)}
                className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors"
              >
                <Icon className={cn('h-4 w-4', color)} />
                <span className="font-medium text-sm flex-1 text-left">{name}</span>
                {scoreInfo && (
                  <Badge variant="outline" className={cn("text-xs", getScoreColor(scoreInfo.score))}>
                    {Math.round(scoreInfo.score * 100)}%
                  </Badge>
                )}
                {model && (
                  <Badge variant="secondary" className="text-xs">
                    {getModelShortName(model)}
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  {formatDuration(durationMs)}
                </span>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {isExpanded && (
                <div className="px-3 pb-3 space-y-3 border-t bg-muted/20">
                  {/* Score Details */}
                  {scoreInfo?.details && Object.keys(scoreInfo.details).length > 0 && (
                    <div className="pt-3">
                      <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                        Score Breakdown
                        {scoreInfo.judgeType === 'llm' && (
                          <span className="ml-2 text-blue-600 font-normal">(AI Judge)</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {Object.entries(scoreInfo.details).map(([metricKey, value]) => (
                          <div key={metricKey} className="text-center p-2 bg-background rounded">
                            <div className="text-xs text-muted-foreground capitalize">
                              {metricKey.replace(/_/g, ' ')}
                            </div>
                            <div className={cn(
                              "font-bold",
                              value >= 0.8 ? "text-green-600" :
                              value >= 0.6 ? "text-yellow-600" : "text-red-600"
                            )}>
                              {Math.round(value * 100)}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Judge Reasoning */}
                  {scoreInfo?.reasoning && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <div className="text-xs font-semibold text-blue-800 dark:text-blue-200 uppercase mb-2">
                        AI Judge Reasoning
                      </div>
                      <div className="text-sm text-blue-900 dark:text-blue-100 whitespace-pre-wrap">
                        {scoreInfo.reasoning}
                      </div>
                    </div>
                  )}

                  <PhaseDetails phaseKey={key} trace={trace} getModelShortName={getModelShortName} />
                </div>
              )}
            </div>
          );
        })}

        {/* Error phase if any */}
        {trace.error && (
          <div className="border border-red-200 rounded-lg p-3 bg-red-50">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span className="font-medium text-sm">Error in {trace.error.phase}</span>
            </div>
            <p className="text-xs text-red-500 mt-1">{trace.error.message}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface PhaseDetailsProps {
  phaseKey: string;
  trace: ConversationTrace;
  getModelShortName: (model?: string) => string;
}

function PhaseDetails({ phaseKey, trace, getModelShortName }: PhaseDetailsProps) {
  if (phaseKey === 'phase1' && trace.phase1) {
    const phase1 = trace.phase1;
    return (
      <div className="space-y-3 pt-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Input</div>
            <div className="bg-background p-3 rounded border min-h-[80px]">
              <p className="text-sm">{phase1.data.originalQuery as string}</p>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Output</div>
            <div className="bg-background p-3 rounded border min-h-[80px] space-y-2">
              {phase1.data.enrichedQuery && phase1.data.enrichedQuery !== phase1.data.originalQuery && (
                <div>
                  <span className="text-xs text-muted-foreground">Enriched:</span>
                  <p className="text-sm">{phase1.data.enrichedQuery as string}</p>
                </div>
              )}
              {phase1.data.searchTerms && (phase1.data.searchTerms as string[]).length > 0 && (
                <div>
                  <span className="text-xs text-muted-foreground">Search Terms:</span>
                  <p className="text-sm">{(phase1.data.searchTerms as string[]).join(', ')}</p>
                </div>
              )}
              {phase1.data.detectedAbbreviations && (phase1.data.detectedAbbreviations as Array<{abbrev: string; full: string}>).length > 0 && (
                <div>
                  <span className="text-xs text-muted-foreground">Abbreviations:</span>
                  <p className="text-sm">{(phase1.data.detectedAbbreviations as Array<{abbrev: string; full: string}>).map(a => `${a.abbrev} → ${a.full}`).join(', ')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {phase1.inputTokens !== undefined && (
          <DetailRow label="Tokens" value={`${phase1.inputTokens} in / ${phase1.outputTokens || 0} out`} />
        )}
      </div>
    );
  }

  if (phaseKey === 'phase2' && trace.phase2) {
    const phase2 = trace.phase2;
    const allResults = phase2.data.allResults as SearchResultWithContent[] | undefined;
    return (
      <div className="space-y-3 pt-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Input</div>
            <div className="bg-background p-3 rounded border min-h-[80px]">
              <p className="text-sm text-muted-foreground">Query embedding generated from search terms</p>
              {phase2.embeddingModel && (
                <p className="text-xs mt-2">Model: {getModelShortName(phase2.embeddingModel)}</p>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Output</div>
            <div className="bg-background p-3 rounded border min-h-[80px]">
              <p className="text-sm">{phase2.data.totalResults || 0} documents found</p>
              <p className="text-sm">Top score: {(phase2.data.topScore as number)?.toFixed(3) || '—'}</p>
            </div>
          </div>
        </div>
        {/* Expandable search results with content preview */}
        {allResults && allResults.length > 0 && (
          <ExpandableSearchResults results={allResults} />
        )}
      </div>
    );
  }

  if (phaseKey === 'phase3' && trace.phase3) {
    const phase3 = trace.phase3;
    return (
      <div className="space-y-3 pt-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Input</div>
            <div className="bg-background p-3 rounded border min-h-[80px]">
              <p className="text-sm text-muted-foreground">Query + retrieved documents for relevance assessment</p>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Output</div>
            <div className="bg-background p-3 rounded border min-h-[80px] space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Has Results:</span>
                <Badge variant={phase3.data.hasResults ? 'default' : 'secondary'}>
                  {phase3.data.hasResults ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Relevant Topic:</span>
                <Badge variant={phase3.data.isRelevantTopic ? 'default' : 'secondary'}>
                  {phase3.data.isRelevantTopic ? 'Yes' : 'No'}
                </Badge>
              </div>
              <p className="text-xs">Response Type: {phase3.data.responseType as string || '—'}</p>
            </div>
          </div>
        </div>
        {phase3.model && (
          <DetailRow label="Model" value={getModelShortName(phase3.model)} />
        )}
      </div>
    );
  }

  if (phaseKey === 'phase4a' && trace.phase4) {
    return <ReasonerPhaseDetailsInline trace={trace} getModelShortName={getModelShortName} />;
  }

  if (phaseKey === 'phase4b' && trace.phase4) {
    return <FormatterPhaseDetailsInline trace={trace} getModelShortName={getModelShortName} />;
  }

  return null;
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-muted-foreground w-28 shrink-0">{label}:</span>
      <span className="text-foreground break-words">{value}</span>
    </div>
  );
}

// Reasoner phase with Input/Output side-by-side
function ReasonerPhaseDetailsInline({
  trace,
  getModelShortName,
}: {
  trace: ConversationTrace;
  getModelShortName: (model?: string) => string;
}) {
  const [activeTab, setActiveTab] = useState<'thinking' | 'output'>('output');
  const phase4 = trace.phase4!;
  const allResults = trace.phase2?.data.allResults as SearchResultWithContent[] | undefined;

  return (
    <div className="space-y-3 pt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Input (Context)</div>
          <div className="bg-background p-3 rounded border min-h-[100px] max-h-[400px] overflow-y-auto">
            {trace.phase1?.data.originalQuery && (
              <div className="mb-3">
                <span className="text-xs text-muted-foreground">Query:</span>
                <p className="text-sm font-medium">{trace.phase1.data.originalQuery as string}</p>
              </div>
            )}
            {allResults && allResults.length > 0 ? (
              <ExpandableSearchResults results={allResults.slice(0, 5)} />
            ) : (
              <p className="text-sm text-muted-foreground">No retrieved documents available</p>
            )}
            <div className="mt-3 pt-2 border-t text-xs text-muted-foreground">
              {phase4.reasonerModel && (
                <p>Model: {getModelShortName(phase4.reasonerModel)}</p>
              )}
              {phase4.reasonerInputTokens !== undefined && (
                <p>Input tokens: {phase4.reasonerInputTokens}</p>
              )}
            </div>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase">Output</span>
            <div className="flex gap-1 border rounded p-0.5">
              <button
                onClick={() => setActiveTab('thinking')}
                className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                  activeTab === 'thinking'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Thinking
              </button>
              <button
                onClick={() => setActiveTab('output')}
                className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                  activeTab === 'output'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Answer
              </button>
            </div>
          </div>
          <div className="bg-background p-3 rounded border min-h-[100px] max-h-[200px] overflow-y-auto">
            {activeTab === 'thinking' ? (
              phase4.data.reasonerThinking ? (
                <pre className="text-xs whitespace-pre-wrap font-mono">{phase4.data.reasonerThinking as string}</pre>
              ) : (
                <p className="text-xs text-muted-foreground italic">No thinking data available</p>
              )
            ) : (
              phase4.data.reasonerAnswer ? (
                <p className="text-sm whitespace-pre-wrap">{phase4.data.reasonerAnswer as string}</p>
              ) : (
                <p className="text-xs text-muted-foreground italic">No answer available</p>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Formatter phase with Input/Output side-by-side
function FormatterPhaseDetailsInline({
  trace,
  getModelShortName,
}: {
  trace: ConversationTrace;
  getModelShortName: (model?: string) => string;
}) {
  const [outputFormat, setOutputFormat] = useState<'raw' | 'preview'>('preview');
  const phase4 = trace.phase4!;

  return (
    <div className="space-y-3 pt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-semibold text-muted-foreground uppercase mb-2">Input (Reasoner Answer)</div>
          <div className="bg-muted/50 p-3 rounded border min-h-[100px] max-h-[200px] overflow-y-auto">
            {phase4.data.reasonerAnswer ? (
              <p className="text-sm whitespace-pre-wrap">{phase4.data.reasonerAnswer as string}</p>
            ) : (
              <p className="text-xs text-muted-foreground italic">No input available</p>
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-muted-foreground uppercase">Output</span>
            <div className="flex gap-1 border rounded p-0.5">
              <button
                onClick={() => setOutputFormat('raw')}
                className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                  outputFormat === 'raw'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Raw
              </button>
              <button
                onClick={() => setOutputFormat('preview')}
                className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                  outputFormat === 'preview'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Slack Preview
              </button>
            </div>
          </div>
          <div className="bg-background rounded border min-h-[100px] max-h-[200px] overflow-y-auto">
            {phase4.data.formattedResponse ? (
              outputFormat === 'raw' ? (
                <pre className="text-xs p-3 whitespace-pre-wrap font-mono">{phase4.data.formattedResponse as string}</pre>
              ) : (
                <SlackPreview
                  blocks={[]}
                  formattedText={phase4.data.formattedResponse as string}
                />
              )
            ) : (
              <p className="text-xs p-3 text-muted-foreground italic">No output available</p>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-4 text-xs">
        {phase4.formatterModel && (
          <span className="text-muted-foreground">Model: {getModelShortName(phase4.formatterModel)}</span>
        )}
        <span className="text-muted-foreground">
          Fallback: <Badge variant={phase4.data.fallbackUsed ? 'destructive' : 'secondary'} className="text-[10px] px-1">
            {phase4.data.fallbackUsed ? 'Yes' : 'No'}
          </Badge>
        </span>
      </div>
    </div>
  );
}

function QualityInsights({ trace }: { trace: ConversationTrace }) {
  const insights: Array<{ type: 'success' | 'warning' | 'info'; message: string }> = [];

  // Analyze timing
  const totalMs = trace.summary?.totalDurationMs || 0;
  if (totalMs > 8000) {
    insights.push({ type: 'warning', message: `Slow response time: ${(totalMs / 1000).toFixed(1)}s total` });
  } else if (totalMs < 3000) {
    insights.push({ type: 'success', message: `Fast response: ${(totalMs / 1000).toFixed(1)}s total` });
  }

  // Check search quality
  const topScore = trace.phase2?.data.topScore as number | undefined;
  if (topScore !== undefined) {
    if (topScore < 0.35) {
      insights.push({ type: 'warning', message: `Low search relevance: top score ${topScore.toFixed(3)}` });
    } else if (topScore > 0.6) {
      insights.push({ type: 'success', message: `Strong search match: top score ${topScore.toFixed(3)}` });
    }
  }

  // Check results
  const totalResults = trace.phase2?.data.totalResults || 0;
  if (totalResults === 0) {
    insights.push({ type: 'warning', message: 'No documents retrieved from vector search' });
  }

  // Check fallback
  if (trace.phase4?.data.fallbackUsed) {
    insights.push({ type: 'warning', message: 'Fallback response was used (primary models may have failed)' });
  }

  // Response type
  if (trace.phase3?.data.responseType === 'no_results_off_topic') {
    insights.push({ type: 'info', message: 'Query was determined to be off-topic' });
  }

  if (insights.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          Quality Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {insights.map((insight, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center gap-2 text-xs p-2 rounded',
              insight.type === 'success' && 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400',
              insight.type === 'warning' && 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
              insight.type === 'info' && 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
            )}
          >
            {insight.type === 'success' && <CheckCircle2 className="h-3 w-3" />}
            {insight.type === 'warning' && <AlertCircle className="h-3 w-3" />}
            {insight.type === 'info' && <MessageSquare className="h-3 w-3" />}
            <span>{insight.message}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
